import { Dayjs } from 'dayjs';
import {
  DataModelBuilder,
  SerializationContext, SerializedData,
} from '../../dto/data-model.js';
import { BusinessModuleRef } from '../business-module-ref.js';
import {
  BusinessLevelMarketingSubscriptionType,
} from './business-level-marketing-subscription-type.js';
import {
  BusinessLevelMarketingSubscription,
} from './business-level-marketing-subscription.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { cloneData, isSameData } from '../../dto/data-model-utils.js';
import { TimeZone } from '../../../time/timezone.js';
import { fromEnumAsString } from '../../../serialization/enum-utils.js';

export class MailerliteSubscription extends BusinessLevelMarketingSubscription {
  static readonly TYPE_ID: string = '2d3a5008630b4f84929f08fd4e36dc85';

  private constructor(
    public readonly id: string,
    public readonly email: string,
    public readonly businessRef: BusinessModuleRef,
    public readonly consentDate: Dayjs,
    public readonly subscriptionDate: Dayjs,
  ) {
    super();
  }

  get $typeId(): string {
    return MailerliteSubscription.TYPE_ID;
  }

  get type(): BusinessLevelMarketingSubscriptionType {
    return BusinessLevelMarketingSubscriptionType.Mailerlite;
  }

  static create(args: {
    id: string,
    email: string,
    businessRef: BusinessModuleRef,
    consentDate: Dayjs,
    subscriptionDate: Dayjs,
  }): MailerliteSubscription {
    return new MailerliteSubscription(
        args.id,
        args.email,
        args.businessRef,
        args.consentDate,
        args.subscriptionDate,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): MailerliteSubscription {
    return this.create({
      id: MapUtils.getString(map, 'id'),
      email: MapUtils.getString(map, 'email'),
      businessRef: BusinessModuleRef.mustDecode(MapUtils.getString(map, 'businessRef')),
      consentDate: context.decodeDate(MapUtils.getNumber(map, 'consentDate')),
      subscriptionDate: context.decodeDate(MapUtils.getNumber(map, 'subscriptionDate')),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      type: fromEnumAsString(this.type),
      id: this.id,
      email: this.email,
      businessRef: this.businessRef.encode(),
      consentDate: context.encodeDate(this.consentDate),
      subscriptionDate: context.encodeDate(this.subscriptionDate),
    };
  }

  clone(): this {
    return MailerliteSubscription.create({
      id: this.id,
      email: this.email,
      businessRef: cloneData(this.businessRef),
      consentDate: TimeZone.cloneDate(this.consentDate),
      subscriptionDate: TimeZone.cloneDate(this.subscriptionDate),
    }) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.id === this.id &&
        other.email === this.email &&
        isSameData(other.businessRef, this.businessRef) &&
        other.consentDate === this.consentDate &&
        other.subscriptionDate === this.subscriptionDate;
  }
}

export class MailerliteSubscriptionBuilder extends DataModelBuilder<MailerliteSubscription> {
  build(context: SerializationContext, map: SerializedData): MailerliteSubscription {
    return MailerliteSubscription.fromMap(context, map);
  }
}
